import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Ticket, SpecialTicketData } from '../shared/models/ticket';
import { PassengerType } from '../shared/models/passenger-types';


export interface ContactDetails {
  type: 'phone' | 'email';
  isValid: boolean;
  countryCode: string;
  phoneNumber: string;
  emailAddress: string;
}

export interface PassengerSelections {
  [passengerType: string]: number;
}

export interface DateDetails {
  startDate: string | null;
  validToDate: string;
}

export enum PaymentMethodType {
  FRISBII_VIPPS = 'FRISBII_VIPPS',
  ApplePay = 'APPLE_PAY',
  PaymentCard = 'REEPAY',
  Free = 'Free'
}

@Injectable({ providedIn: 'root' })
export class PurchaseFormService {

  private termsAccepted = new BehaviorSubject<boolean>(false);
  public paymentMethod = new BehaviorSubject<PaymentMethodType>(PaymentMethodType.FRISBII_VIPPS);

  private contactDetails: ContactDetails = { type: 'phone', isValid: false, countryCode: '+45', phoneNumber: '', emailAddress: '' };
  private passengerSelections: PassengerSelections = {};
  private dateDetails: DateDetails = { startDate: '', validToDate: '' };

  private ticketsSubject = new BehaviorSubject<Ticket[]>([]);
  public tickets$ = this.ticketsSubject.asObservable();

  private readonly PAYMENT_METHOD_KEY = 'rejsebillet_lastPayment';
  private readonly FORM_STATE_KEY = 'rejsebillet_formState';

  constructor() {
    this.loadPreferencesFromStorage();
  }

  getTermsAccepted() { return this.termsAccepted.value; }
  getPaymentMethod() { return this.paymentMethod.value; }
  getContactDetails() { return this.contactDetails; }
  getPassengerSelections() { return this.passengerSelections; }
  getDateDetails() { return this.dateDetails; }

  setContactDetails(details: ContactDetails): void {
    this.contactDetails = details;
  }

  setDateDetails(details: DateDetails): void {
    this.dateDetails = details;
  }

  setTermsAccepted(accepted: boolean): void {
    this.termsAccepted.next(accepted);
  }

  setPaymentMethod(method: PaymentMethodType): void {
    this.paymentMethod.next(method);
    this.savePaymentPreference(method);
  }

  isFormValid(): boolean {
    if (!this.hasTickets() || this.getTicketAmount() > 30) return false;
    if (!this.termsAccepted.value) return false;
    if (!this.contactDetails.isValid) return false;
    if (!this.isDateValid()) return false;

    if (!this.isFreeOrder() && this.paymentMethod.value === PaymentMethodType.FRISBII_VIPPS && this.contactDetails.type !== 'phone') {
      return false;
    }
    return true;
  }

  private isDateValid(): boolean {
    const { startDate, validToDate } = this.dateDetails;

    if (!startDate || !validToDate) return false;

    const start = new Date(startDate);
    const end = new Date(validToDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
    if (start >= end) return false;

    return true;
  }

  initializeTickets(data: SpecialTicketData): void {
    const savedCounts = this.passengerSelections;
    const passengerTypes = Object.values(PassengerType);

    const tickets: Ticket[] = (data?.pricingOptions || [])
      .filter(option => passengerTypes.includes(option.passengerType as PassengerType))
      .map(option => ({
        id: `${data.id}-${option.passengerType}`,
        type: option.passengerType,
        passengerType: option.passengerType,
        price: option.price,
        count: savedCounts[option.passengerType] ?? 0,
        specialTicketProductId: data.id,
        overriddenPassengerTypes: data.overriddenPassengerTypes
      }));

    const sortedTickets = tickets.sort((a, b) =>
      passengerTypes.indexOf(a.passengerType) -
      passengerTypes.indexOf(b.passengerType)
    );
    this.ticketsSubject.next(sortedTickets);
  }

  increase(ticket: Ticket): void {
    const tickets = this.ticketsSubject.value;
    const index = tickets.findIndex(t => t.id === ticket.id);
    if (index !== -1) {
      tickets[index].count++;
      this.ticketsSubject.next([...tickets]);
    }
  }

  decrease(ticket: Ticket): void {
    const tickets = this.ticketsSubject.value;
    const index = tickets.findIndex(t => t.id === ticket.id);
    if (index !== -1 && tickets[index].count > 0) {
      tickets[index].count--;
      this.ticketsSubject.next([...tickets]);
    }
  }

  hasTickets(): boolean {
    return this.ticketsSubject.value.some(t => t.count > 0);
  }

  getSelectedAmountForPassengerType(passengerType: string): number {
    return this.passengerSelections[passengerType];
  }

  getTotalAmount(): number {
    return this.ticketsSubject.value.reduce((total, ticket) =>
      total + (ticket.price * ticket.count), 0
    );
  }

  isFreeOrder(): boolean {
    return this.getTotalAmount() === 0;
  }

  getTicketAmount(): number {
    const totalAmount = this.ticketsSubject.value.reduce((total, ticket) =>
      (total + ticket.count), 0
    );
    return totalAmount;
  }

  saveFormState(): void {
    const state = {
      contactDetails: this.contactDetails,
      passengerSelections: this.ticketsSubject.value.reduce((acc, t) => {
        acc[t.passengerType] = t.count;
        return acc;
      }, {} as PassengerSelections),
      dateDetails: this.dateDetails,
      termsAccepted: this.termsAccepted.value,
      paymentMethod: this.paymentMethod.value
    };
    sessionStorage.setItem(this.FORM_STATE_KEY, JSON.stringify(state));
  }

  restoreFormState(): boolean {
    const saved = sessionStorage.getItem(this.FORM_STATE_KEY);
    if (!saved) return false;

    try {
      const state = JSON.parse(saved);
      if (state.contactDetails) this.contactDetails = state.contactDetails;
      if (state.dateDetails) this.dateDetails = state.dateDetails;
      if (state.paymentMethod) this.paymentMethod.next(state.paymentMethod);
      if (state.passengerSelections) this.passengerSelections = state.passengerSelections;
      this.termsAccepted.next(false);
      return true;
    } catch (e) {
      console.error('Error restoring form state:', e); //TODO: Remove test phase of QR release
      return false;
    }
  }

  clearSavedFormState(): void {
    sessionStorage.removeItem(this.FORM_STATE_KEY);
  }

  getSavedPaymentMethod(): PaymentMethodType {
    return localStorage.getItem(this.PAYMENT_METHOD_KEY) as PaymentMethodType;
  }

  private savePaymentPreference(method: PaymentMethodType): void {
    localStorage.setItem(this.PAYMENT_METHOD_KEY, method);
  }

  private loadPreferencesFromStorage(): void {
    const savedMethod = localStorage.getItem(this.PAYMENT_METHOD_KEY) as PaymentMethodType;
    if (savedMethod) {
      this.paymentMethod.next(savedMethod);
    }
  }

  clearFormState(): void {
    this.contactDetails = { type: 'phone', isValid: false, countryCode: '+45', phoneNumber: '', emailAddress: '' };
    this.passengerSelections = {};
    this.dateDetails = { startDate: '', validToDate: '' };
    this.termsAccepted.next(false);
    this.clearSavedFormState();

    const resetTickets = this.ticketsSubject.value.map(t => ({ ...t, count: 0 }));
    this.ticketsSubject.next(resetTickets);
  }
}
